import { shallowEqual, useSelector } from 'react-redux';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import PropTypes from 'prop-types';
import { Provider } from './context';
import { truthyOrZero } from '@/helpers/math';
import { selectAudioMuted, selectAudioVolume } from '@/state/selectors/audio';
import { selectCacheHydrationComplete } from '@/state/selectors/user';
import useAudioElement from '@/hooks/audio/useAudioElement';
import useAudioReduxControls from '@/hooks/audio/useAudioReduxControls';
import useShouldRenderForMobile from '@/hooks/layout/useShouldRenderForMobile';

const selector = (state) => ({
  volume: selectAudioVolume(state),
  muted: selectAudioMuted(state),
  cacheHydrated: selectCacheHydrationComplete(state)
});

const AudioVolumeProvider = ({ children }) => {
  const shouldRenderForMobile = useShouldRenderForMobile();
  const volumeSetFromStore = useRef(false);
  const { audioRef, audioRefReady } = useAudioElement();
  const { handleSaveVolume: handleSaveVolumeInStore, handleMute } = useAudioReduxControls();
  const {
    volume: volumeFromStore, cacheHydrated, muted
  } = useSelector(selector, shallowEqual);

  const [volume, setVolume] = useState(1);

  useEffect(() => {
    if (audioRefReady && cacheHydrated && !volumeSetFromStore.current) {
      if (truthyOrZero(volumeFromStore)) {
        audioRef.current.volume = volumeFromStore;
        setVolume(volumeFromStore);
      }
      volumeSetFromStore.current = true;
    }
  }, [volumeFromStore, audioRef, audioRefReady, cacheHydrated]);

  useEffect(() => {
    if (audioRefReady && volumeSetFromStore.current) {
      audioRef.current.volume = volume;
    }
  }, [volume, audioRef, audioRefReady]);

  const handleChangeVolume = useCallback(({ vol }) => {
    if (truthyOrZero(vol)) {
      setVolume(vol);
    }
  }, []);

  const handleSaveVolume = useCallback(({ vol }) => {
    if (truthyOrZero(vol)) {
      handleChangeVolume({ vol });
      handleSaveVolumeInStore(vol);
    }
  }, [handleChangeVolume, handleSaveVolumeInStore]);

  const handleToggleMute = useCallback(() => {
    handleMute(!muted);
  }, [muted, handleMute]);

  // Unmute the audio when switching to mobile as there is no interface to unmute it
  useEffect(() => {
    if (shouldRenderForMobile && muted) {
      handleToggleMute();
    }
  }, [shouldRenderForMobile, handleToggleMute, muted]);

  const value = useMemo(() => ({
    liveVolume: volume,
    handleChangeVolume,
    handleSaveVolume,
    muted,
    handleToggleMute
  }), [volume, handleChangeVolume, handleSaveVolume, muted, handleToggleMute]);

  return (
    <Provider value={ value }>
      { children }
    </Provider>
  );
};

AudioVolumeProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node
  ]).isRequired
};

export default AudioVolumeProvider;